import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText, Upload, File, Loader2, AlertCircle, CheckCircle, Eye, Download,
  BookOpen, FileSignature, RefreshCw,
} from 'lucide-react';
import { documentsApi, mattersApi } from '../services/api';
import { useMattersStore } from '../store/mattersStore';
import { Document } from '../types';

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Processing status, in words (spec §4.5 / §6). Uploads OCR lazily at first
 *  use, so an "uploaded" file is genuinely ready to attach. */
function statusMeta(status: string): { label: string; cls: string; failed: boolean } {
  if (status === 'failed') {
    return { label: "Couldn't read", cls: 'bg-red-50 text-red-700', failed: true };
  }
  if (status === 'processing') {
    return { label: 'Reading the text…', cls: 'bg-blue-50 text-blue-700', failed: false };
  }
  return { label: 'Ready', cls: 'bg-green-50 text-green-700', failed: false };
}

const ROW_ACTION_CLS =
  'inline-flex items-center gap-1 text-xs font-medium text-legal-500 hover:text-accent-700 transition-colors';

export function DocumentsPage() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [dragOver, setDragOver] = useState(false);
  // "All documents" library view (IA, July 2026): All | Not filed. The
  // documents API doesn't carry matter_id, so filed-ness is derived from the
  // case files themselves — each matter detail lists its documents.
  const [view, setView] = useState<'all' | 'unfiled'>('all');
  const [isResolvingFiled, setIsResolvingFiled] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { matters, fetchMatters, detailById, setMatterDetail } = useMattersStore();

  // Document upload is included on EVERY plan (per-research pricing: all plans
  // share the same features) — no tier gate here, matching the backend.
  useEffect(() => {
    documentsApi.getDocuments()
      .then(({ data }) => setDocs(Array.isArray(data) ? data : []))
      .catch(() => setDocs([]))
      .finally(() => setIsLoading(false));
    fetchMatters();
  }, [fetchMatters]);

  // On first switch to "Not filed", hydrate details for cases that hold
  // documents (cache-then-revalidate store — revisits are free). Best-effort:
  // a failed detail fetch just leaves that case's documents in "Not filed".
  useEffect(() => {
    if (view !== 'unfiled') return;
    const missing = matters.filter(
      (m) => (m.documents_count ?? 0) > 0 && !detailById[m.id],
    );
    if (missing.length === 0) return;
    let stale = false;
    setIsResolvingFiled(true);
    Promise.all(
      missing.map((m) =>
        mattersApi
          .get(m.id)
          .then(({ data }) => setMatterDetail(data))
          .catch(() => {}),
      ),
    ).finally(() => {
      if (!stale) setIsResolvingFiled(false);
    });
    return () => {
      stale = true;
    };
  }, [view, matters, detailById, setMatterDetail]);

  // Every document id that appears in any case file.
  const filedIds = new Set(
    Object.values(detailById).flatMap((d) => d.documents.map((doc) => doc.id)),
  );
  const shownDocs = view === 'unfiled' ? docs.filter((d) => !filedIds.has(d.id)) : docs;

  const handleUpload = async (file: File) => {
    if (!file) return;
    // One upload rule everywhere (spec §4.5): 50MB, PDF/DOCX/TXT — same as
    // the FramingChat and DraftIntakeChat attach controls.
    const allowed = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
    ];
    if (!allowed.includes(file.type)) {
      setUploadError('Only PDF, DOCX and TXT files are supported.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setUploadError('File must be under 50 MB.');
      return;
    }
    setUploadError('');
    setUploadSuccess('');
    setIsUploading(true);
    try {
      const { data } = await documentsApi.uploadDocument(file);
      setDocs(prev => [data, ...prev]);
      setUploadSuccess(`"${file.name}" uploaded successfully.`);
      setTimeout(() => setUploadSuccess(''), 4000);
    } catch {
      setUploadError('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-legal-900">All documents</h1>
          <p className="text-legal-500 text-sm mt-1">
            Documents live inside their case files — this is everything, including ones not
            filed under a case yet.
          </p>
        </div>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-accent text-legal-900 font-semibold rounded-lg hover:bg-accent-400 transition-colors disabled:opacity-60 text-sm"
        >
          {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          <span>Upload</span>
        </button>
      </div>

      <>
          {/* Alerts */}
          {uploadError && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{uploadError}</span>
            </div>
          )}
          {uploadSuccess && (
            <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              <span>{uploadSuccess}</span>
            </div>
          )}

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              dragOver
                ? 'border-accent bg-accent/5'
                : 'border-legal-300 hover:border-accent hover:bg-legal-50'
            }`}
          >
            <Upload className={`h-8 w-8 mx-auto mb-3 ${dragOver ? 'text-accent' : 'text-legal-400'}`} />
            <p className="text-legal-700 font-medium text-sm">
              {isUploading ? 'Uploading...' : 'Drag & drop or click to upload'}
            </p>
            <p className="text-legal-400 text-xs mt-1">PDF, DOCX, TXT — up to 50 MB</p>
          </div>

          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
            className="hidden"
            onChange={onFileInput}
          />

          {/* All | Not filed toggle (documents not yet in any case file) */}
          <div className="flex items-center gap-1.5">
            {(
              [
                { value: 'all', label: 'All' },
                { value: 'unfiled', label: 'Not filed' },
              ] as const
            ).map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setView(f.value)}
                data-track={`documents-filter-${f.value}`}
                className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors press ${
                  view === f.value
                    ? 'bg-accent text-legal-900'
                    : 'bg-white border border-legal-200 text-legal-600 hover:border-legal-300'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Document list */}
          {isLoading || (view === 'unfiled' && isResolvingFiled) ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="bg-white border border-legal-200 rounded-xl p-4 animate-pulse">
                  <div className="h-4 bg-legal-100 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-legal-100 rounded w-1/4" />
                </div>
              ))}
            </div>
          ) : shownDocs.length === 0 ? (
            <div className="text-center py-12 bg-white border border-dashed border-legal-300 rounded-xl">
              <File className="h-10 w-10 text-legal-300 mx-auto mb-3" />
              <p className="text-legal-600 font-medium">
                {view === 'unfiled' && docs.length > 0
                  ? 'Everything is filed'
                  : 'No documents yet'}
              </p>
              <p className="text-legal-400 text-sm mt-1">
                {view === 'unfiled' && docs.length > 0
                  ? 'Every document here already lives in a case file.'
                  : 'Attach a document while asking a legal question or drafting — or upload one above.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {shownDocs.map((doc) => {
                const status = statusMeta(doc.processing_status);
                return (
                  <div key={doc.id} className="bg-white border border-legal-200 rounded-xl p-4">
                    <div className="flex items-center space-x-4">
                      <div className="p-2.5 bg-legal-50 rounded-lg flex-shrink-0">
                        <FileText className="h-5 w-5 text-legal-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-legal-900 font-medium text-sm truncate">{doc.original_filename}</p>
                        <p className="text-legal-400 text-xs mt-0.5">
                          {formatBytes(doc.file_size_bytes)} · {formatDate(doc.created_at)}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${status.cls}`}>
                        {status.label}
                      </span>
                    </div>
                    {/* From dead end to library (spec §4.5): every row offers
                        its next steps, reusing the existing intake plumbing. */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 pl-[3.25rem]">
                      <Link
                        to={`/app/documents/${doc.id}`}
                        data-track="document-row-view"
                        className={ROW_ACTION_CLS}
                      >
                        <Eye className="h-3.5 w-3.5" /> View
                      </Link>
                      {doc.download_url && (
                        <a
                          href={doc.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-track="document-row-download"
                          className={ROW_ACTION_CLS}
                        >
                          <Download className="h-3.5 w-3.5" /> Download
                        </a>
                      )}
                      {status.failed ? (
                        <button
                          type="button"
                          onClick={() => inputRef.current?.click()}
                          data-track="document-row-retry"
                          className={ROW_ACTION_CLS}
                        >
                          <RefreshCw className="h-3.5 w-3.5" /> Retry (upload it again)
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              navigate('/app/research/new', {
                                state: { doc: { id: doc.id, name: doc.original_filename } },
                              })
                            }
                            data-track="document-row-research"
                            className={ROW_ACTION_CLS}
                          >
                            <BookOpen className="h-3.5 w-3.5" /> Research this document
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              navigate('/app/drafts/new', {
                                state: { doc: { id: doc.id, name: doc.original_filename } },
                              })
                            }
                            data-track="document-row-draft-reply"
                            className={ROW_ACTION_CLS}
                          >
                            <FileSignature className="h-3.5 w-3.5" /> Draft a reply
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </>
    </div>
  );
}
